import { CommonModule, DecimalPipe } from "@angular/common";
import { Component, signal } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorIntl } from "@angular/material/paginator";
import {
  MatSidenavContainer,
  MatSidenavModule,
} from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import {
  ExportDialogComponent,
  ExportOptions,
} from "app/layout/export-overlay";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { NoteDetailModalComponent } from "app/modules/shared/note-detail-modal/note-detail-modal.component";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { DecimalConfigService } from "app/services/decimal-config.service";
import {
  ColDef,
  GridConfig,
  PageChangedEvent,
  SmartGridComponent,
  SortChangedEvent,
} from "app/shared/components/smart-grid";
import { CurrencyCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component";
import { CustomerCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component";
import { DateHourCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component";
import { DeliveredQuantityCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/delivered-quantity-cell-renderer/delivered-quantity-cell-renderer.component";
import { LossQuantityCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/loss-quantity-cell-renderer/loss-quantity-cell-renderer.component";
import { MaterialCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component";
import { TripNavigationCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/trip-navigation-cell-renderer/trip-navigation-cell-renderer.component";
import { UserRouteCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/user-route-cell-renderer/user-route-cell-renderer.component";
import { DateHelper } from "app/utils/date.helper";
import { getCode } from "app/utils/method";
import {
  LOSS_EXPORT_ID,
  LOSS_PROPERTIES,
  LOSS_TITLE,
} from "../../assistant/delivery/loads/exports/reports/loss-report.constants";
import { LossReportExport } from "../../assistant/delivery/loads/exports/reports/loss-report.export";
import { CompanyService } from "../../configuration/company/company.service.service";
import { BusinessPartnersFiltersComponent } from "../../filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "../../filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "../../filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "../../filters/documents-filters/documents-filters.component";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { MaterialsManagementFiltersComponent } from "../../filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "../../filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "../../filters/status-filters/status-filters.component";
import { OrderCustomerService } from "../historic/order-customer/order-customer.service";
import { lossInfo, lossResponse } from "./dto/loss-report.dto";
@Component({
  selector: "app-loss-report",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    HoverMenuDirective,
    MatMenuModule,
    FilterComponent,
    FilterSectionComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    NoteDetailModalComponent,
    MatTooltipModule,
    CompanyCurrencyPipe,
    SmartGridComponent,
  ],
  templateUrl: "./loss-report.component.html",
  styleUrl: "./loss-report.component.scss",
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    LossReportExport,
    DecimalPipe,
  ],
})
export class LossReportComponent {
  getCode = getCode;
  private decimalPipe = new DecimalPipe("en-US");

  data: lossInfo[] = [];
  columnDefs: ColDef<lossInfo>[] = [];
  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [
        { field: "subRoute.externalKeys.code" as any, direction: "desc" },
      ],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  totalItems = signal<number>(0);
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 20;
  sortActive: keyof lossInfo = "creationDateTime";
  sortDirection: "ASC" | "DESC" = "DESC";

  currentPage = 1;

  UMPActive: boolean;

  form: FormGroup;

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  type = DocumentTypeEnum.DOCUMENTO_MERMA;

  expandedElement: lossInfo | null = null;

  selectedStatuses: number[] = [];

  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  /**
   *
   */
  constructor(
    private fb: FormBuilder,
    private orderCustomerService: OrderCustomerService,
    private snackBar: MatSnackBar,
    private readonly companyService: CompanyService,
    private reportsRouteExport: LossReportExport,
    private router: Router,
    private dialog: MatDialog,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private decimalConfigService: DecimalConfigService
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      subRouteId: [],
      assignedUserId: [],
      customerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
    });
  }

  private formatCurrency(value: number): string {
    if (value == null) return "-";
    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum =
      this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? "-";
    if (formattedNum === "-") return "-";
    const symbol = this.companyCurrencyPipe.transform("symbol");
    return `${symbol} ${formattedNum}`;
  }

  private createCurrencyFilterOptions(fieldPath: string) {
    return {
      valueExtractor: (item: any) => {
        const value = this.getNestedValue(item, fieldPath);
        return this.formatCurrency(value);
      },
      customFilterFn: (rowData: any, filterValue: string) => {
        const value = this.getNestedValue(rowData, fieldPath);
        const formattedValue = this.formatCurrency(value);
        if (!formattedValue || formattedValue === "-") return !filterValue;
        return formattedValue === filterValue;
      },
    };
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        field: "subRoute.externalKeys.code" as any,
        headerName: "Ruta / Usuario",
        minWidth: 300,
        sortable: true,
        cellRenderer: UserRouteCellRendererComponent,
        filterOptions: {
          valueExtractor: (item: any) => {
            const subRoute = item.subRoute;
            const user = item.user;
            const routeName = subRoute?.name ?? "-";
            const userName = user?.name ?? "-";
            return `${routeName} / ${userName}`;
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            const subRoute = rowData.subRoute;
            const user = rowData.user;
            const routeName = subRoute?.name ?? "-";
            const userName = user?.name ?? "-";
            const displayValue = `${routeName} / ${userName}`;
            if (!displayValue || displayValue === "- / -") return !filterValue;
            return displayValue === filterValue;
          },
        },
      },

      {
        field: "trip.id" as any,
        headerName: "Viaje",
        minWidth: 140,
        sortable: true,
        cellRenderer: TripNavigationCellRendererComponent,
      },
      {
        field: "serial",
        headerName: "No. Documento",
        minWidth: 160,
        sortable: true,
      },
      {
        field: "salesOffice.name" as any,
        headerName: "Oficina de ventas",
        minWidth: 160,
        sortable: true,
      },
      {
        field: "detail.createdAt" as any,
        headerName: "Fecha",
        minWidth: 140,
        sortable: true,
        cellRenderer: DateHourCellRendererComponent,
        cellRendererParams: {
          getValue: (data: any) => data?.detail?.createdAt,
          showHour: false,
        },
        filterOptions: {
          valueExtractor: (item: any) => {
            const date = item?.detail?.createdAt;
            if (!date) return "-";
            return new Date(date).toLocaleDateString("es-ES");
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            const date = rowData?.detail?.createdAt;
            if (!date) return !filterValue;
            const formattedDate = new Date(date).toLocaleDateString("es-ES");
            return formattedDate === filterValue;
          },
        },
      },
      {
        field: "sourceTransaction.serial" as any,
        headerName: "No. Entrega",
        minWidth: 140,
        sortable: false,
        valueFormatter: (value: any, data: any) => {
          return data?.sourceTransaction?.serial || "-";
        },
      },
      {
        field: "detail.material.name" as any,
        headerName: "Material",
        minWidth: 200,
        sortable: true,
        cellRenderer: MaterialCellRendererComponent,
        filterOptions: {
          valueExtractor: (item: any) => item.materialName?.name ?? "-",
          customFilterFn: (rowData: any, filterValue: string) => {
            const materialName = rowData.materialName?.name ?? "-";
            if (!materialName || materialName === "-") return !filterValue;
            return materialName === filterValue;
          },
        },
      },
      {
        field: "soldTo.name" as any,
        headerName: "Cliente",
        minWidth: 200,
        sortable: true,
        cellRenderer: CustomerCellRendererComponent,
        filterOptions: {
          valueExtractor: (item: any) => item.shipToName ?? "-",
          customFilterFn: (rowData: any, filterValue: string) => {
            const shipToName = rowData.shipToName ?? "-";
            if (!shipToName || shipToName === "-") return !filterValue;
            return shipToName === filterValue;
          },
        },
      },
      {
        field: "sourceTransaction.soldQuantity" as any,
        headerName: "Cantidad Entregado/Facturado",
        minWidth: 220,
        sortable: false,
        cellRenderer: DeliveredQuantityCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive,
        },
      },
      {
        field: "detail.soldQuantity" as any,
        headerName: "Cantidad Merma",
        minWidth: 200,
        sortable: false,
        cellRenderer: LossQuantityCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive,
        },
      },
      {
        field: "sourceTransaction.mermaPercentage" as any,
        headerName: "% de Merma",
        minWidth: 120,
        sortable: false,
        valueFormatter: (value: any, data: any) => {
          const lossQuantity = data?.detail?.baseQuantity;
          const sourceQuantity = data?.sourceTransaction?.baseQuantity;
          if (lossQuantity == null || !sourceQuantity) return "-";
          const percentage =
            Math.floor((lossQuantity / sourceQuantity) * 100 * 100) / 100;
          return `${percentage} %`;
        },
      },
      {
        field: "detail.unitPrice" as any,
        headerName: "Prc. Neto",
        minWidth: 120,
        sortable: false,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
        filterOptions: this.createCurrencyFilterOptions("detail.unitPrice"),
      },
      {
        field: "detail.total" as any,
        headerName: "Valor neto",
        minWidth: 120,
        sortable: false,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
        filterOptions: this.createCurrencyFilterOptions("detail.total"),
      },
      {
        field: "detail.rejectedQuantitiesOnDelivery" as any,
        headerName: "Motivo de rechazo",
        minWidth: 180,
        sortable: false,
        valueFormatter: (value: any, data: any) => {
          return (
            data?.detail?.rejectedQuantitiesOnDelivery?.[0]?.reasonForRejection
              ?.name || "-"
          );
        },
      },
    ];
  }

  ngOnInit(): void {
    this.initializeColumnDefs();
    this.fetchCompanies();
    this.getListReport();
  }

  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive;
      }
    });
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  onExportButtonPressed(): void {
    let optionsFilter = {
      currentPage: this.currentPage,
      limitPerPage: this.pageSize,
      search: this.search,
      sortDirection: this.sortDirection,
      sort: this.sortActive,
    };

    let filter: IFilters = {
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip:
        DateHelper.getEndOfDay(this.form.get("endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("startTrip")?.value) ??
        null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip:
        DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value) ??
        null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      subRouteId: this.form.get("subRouteId")?.value, //
      customerId: this.form.get("customerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    if (!this.reportsRouteExport.canStartTask(LOSS_EXPORT_ID)) return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: LOSS_TITLE },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.reportsRouteExport.export(
          options.format,
          options.range,
          this.totalItems(),
          this.pageSize,
          this.currentPage,
          LOSS_EXPORT_ID,
          LOSS_TITLE,
          LOSS_PROPERTIES,
          this.UMPActive,
          filter,
          optionsFilter
        );
      }
    });
  }

  onPageChange(event: PageChangedEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  onSortChange(event: SortChangedEvent<lossInfo>): void {
    this.currentPage = 1;
    this.sortActive =
      (event.sortStates[0]?.field as keyof lossInfo) || "creationDateTime";
    this.sortDirection =
      event.sortStates[0]?.direction === "asc" ? "ASC" : "DESC";
    this.getListReport();
  }

  openNoteDetail(element: lossInfo) {
    if (!element.detail.note || element.detail.note.trim() === "") {
      return;
    }

    const modalData = {
      order: element,
      detail: element.detail,
      note: element.detail.note,
      material: element.detail.material,
    };

    const dialogRef = this.dialog.open(NoteDetailModalComponent, {
      data: modalData,
      width: "550px",
      maxWidth: "95vw",
      disableClose: true,
      panelClass: "custom-dialog-container",
    });
  }

  onClearFilter() {
    this.form.reset({
      start: new Date(),
      end: new Date(),
    });
    this.getListReport();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  goToTravel(element: lossInfo) {
    if (element.trip?.id) {
      const queryParams = { tripId: element.trip.id };
      const urlTree = this.router.createUrlTree(
        ["monitor", "principal-screen"],
        { queryParams }
      );
      const absoluteUrl =
        window.location.origin + this.router.serializeUrl(urlTree);
      window.open(absoluteUrl, "_blank");
    }
  }

  getListReport() {
    let options = {
      currentPage: this.currentPage,
      limitPerPage: this.pageSize,
      search: this.search,
      sortDirection: this.sortDirection,
      sort: this.sortActive,
    };

    let filter: IFilters = {
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip:
        DateHelper.getEndOfDay(this.form.get("endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("startTrip")?.value) ??
        null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip:
        DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value) ??
        null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      subRouteId: this.form.get("subRouteId")?.value, //
      seller: this.form.get("assignedUserId")?.value,
      customerId: this.form.get("customerId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    this.orderCustomerService
      .findAllWithCountShrinkages(options, filter)
      .subscribe({
        next: (res: lossResponse) => {
          // Flatten only material and customer data for their cell renderers
          const rawData = res?.data?.data ?? [];
          this.data = rawData.map((item: any) => ({
            ...item,
            // Flatten material data for MaterialCellRenderer
            materialName: item?.detail?.material,
            // Flatten customer data for CustomerCellRenderer
            shipToExternalKeyCode: item?.soldTo?.externalKeys?.code ?? "-",
            shipToName: item?.soldTo?.name ?? "-",
          }));
          this.totalItems.set(res?.data?.pageInfo?.[0]?.total ?? 0);
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener reporte",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }
}
